// src/components/OrganizationTree/OrganizationTree.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactFlow, {
  Controls,
  applyNodeChanges,
  applyEdgeChanges,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import ConfirmedViewChart from "../Modals/ConfirmedViewChart";
import NoConfirmVSView from "../Modals/NoConfirmVSView";
import edit from "../../assets/edit.svg";
import Level1 from "../Questionnaire/Level1";
import Level2 from "../Questionnaire/Level2";
import Level3 from "../Questionnaire/Level3";
import Level4 from "../Questionnaire/Level4";
import Level5 from "../Questionnaire/Level5";
import Level6 from "../Questionnaire/Level6";
import Level7 from "../Questionnaire/Level7";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import { useUser } from "providers/UserProvider";
import { useInterceptor } from "providers/InterceptorProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  updateLevel1,
  updateLevel2,
  updateLevel3,
  updateLevel4,
  updateLevel5,
  updateLevel6,
  updateLevel7,
} from "store/QuestionnaireSlice";
import KPI_menu from "../../assets/KPI_menu.png";
import edit_pen from "../../assets/edit_pen.svg";
import calendar_month from "../../assets/calendar_month.svg";
import AddKPIMetricModal from "components/Modals/KPIMetric/AddKPIMetricModal";
import AddKPIModal from "components/Modals/KPI/AddKPIModal";
import SuccessEdit from "components/Modals/SuccessEditTree/SuccessEdit";
import CustomControl from "./CustomControl";
const Flow = ({
  treeData,
  title,
  index,
  showEditButton,
  handleEditClick,
  showEditQuestions,
  onMenuClick,
}) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [showKPIFormModal, setShowKPIFormModal] = useState(false);
  const [showKPIMetricFormModal, setShowKPIMetricFormModal] = useState(false);
  const [currentNodeData, setCurrentNodeData] = useState({
    nodeId: "",
    heading: "",
  });
  const questionCombination = [
    "Combines 1, 5 and 7 questions",
    "Combines 2 and 4 questions",
    "Combines 3 and 6 questions",
  ];

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  function handleAddKPI(nodeId, heading) {
    setCurrentNodeData({
      nodeId,
      heading,
    });
    setShowKPIFormModal(true);
  }

  function handleAddKPIMetric(nodeId, heading) {
    setCurrentNodeData({
      nodeId,
      heading,
    });
    setShowKPIMetricFormModal(true);
  }

  useEffect(() => {
    if (treeData) {
      createNodesAndEdges(treeData);
    }
  }, [treeData]);

  const createNodesAndEdges = (data) => {
    const newNodes = [];
    const newEdges = [];
    let nodeId = 1;
    const nodeWidth = 200;
    const nodeHeight = 120;

    // Helper function to calculate the total width of a subtree
    const calculateSubtreeWidth = (node, level) => {
      if (!node.children || node.children.length === 0) return nodeWidth;
      return (
        node.children.reduce(
          (sum, child) => sum + calculateSubtreeWidth(child, level + 1),
          0
        ) +
        (node.children.length - 1) * getHorizontalSpacing(level + 1)
      );
    };

    // Function to get horizontal spacing based on level
    const getHorizontalSpacing = (level) => {
      if (level === 2) return -90; // Level 2 has a spacing of -90
      if (level === 3) return -90;
      return -50; // All other levels have a spacing of -50
    };

    const processNode = (node, level = 0, parentId = null, startX) => {
      const currentId = node._id || `node-${nodeId++}`;
      const subtreeWidth = calculateSubtreeWidth(node, level);

      // Calculate the x position of the current node
      const x = startX + subtreeWidth / 2 - nodeWidth / 2;

      newNodes.push({
        id: currentId,
        position: {
          x:
            level === 0
              ? -189 // Root node x-position
              : level === 2
              ? x + 21 // Adjust x-position for level 2
              : level === 3
              ? x + 21 // Adjust x-position for level 3
              : x, // Default x-position for other levels
          y: level * (nodeHeight - 40), // y-position based on level
        },
        data: {
          label: (
            <div className="flex flex-col items-center gap-2 relative">
              <span
                className={`${
                  level === 0
                    ? "text-[16px] font-semibold"
                    : level === 1
                    ? "text-[12px] font-medium"
                    : "text-[12px]"
                }`}
              >
                {node.name}
              </span>
              {node.children?.length === 0 && level >= 2 && (
                <button
                  className="py-1 px-3 w-fit"
                  onClick={(e) => onContextMenu(e, node)}
                >
                  <img src={KPI_menu} alt="" className="" />
                </button>
              )}
            </div>
          ),
          showMenu: false,
        },
        style: {
          display: "flex",
          flexDirection: "column",
          background:
            level === 0 ? "#1C3F3A" : level === 1 ? "#F1FFF6" : "#FFFFFF",
          color: level === 0 ? "white" : "black",
          border: level === 1 ? "1px solid #55C880" : "1px solid #D9D9D9",
          borderRadius: "8px",
          padding: "10px",
          width: level === 0 ? "319px" : level === 1 ? "142px" : "100px",
          textAlign: "center",
        },
      });

      if (parentId) {
        newEdges.push({
          id: `edge-${parentId}-${currentId}`,
          source: parentId,
          target: currentId,
          type: "smoothstep",
          style: {
            stroke: "#1C3F3A",
            strokeWidth: 1,
            strokeDasharray: "5,5",
          },
          pathOptions: {
            offset: 4,
            borderRadius: 0,
          },
        });
      }

      if (node.children) {
        let currentX = startX;
        node.children.forEach((child) => {
          const childWidth = calculateSubtreeWidth(child, level + 1);
          processNode(child, level + 1, currentId, currentX);
          currentX += childWidth + getHorizontalSpacing(level + 1);
        });
      }
    };

    // Calculate the total width of the entire tree and center it
    const totalTreeWidth = calculateSubtreeWidth(data, 0);
    const initialX = -totalTreeWidth / 2;

    processNode(data, 0, null, initialX);
    setNodes(newNodes);
    setEdges(newEdges);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const KPIMenuref = useRef(null);
  const onContextMenu = (e, node) => {
    e.preventDefault();
    // if (isOpen) {
    //   alert("isOpen is ", isOpen);
    //   setIsOpen(false);
    //   setPosition(null);
    //   return;
    // }
    setSelectedNode(node);
    setPosition({ x: e.pageX, y: e.pageY });
    setIsOpen(true);
  };
  const handleKPIModalClose = useCallback(() => {
    setSelectedNode(null);
    setShowKPIFormModal(false);
    setShowKPIMetricFormModal(false);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (KPIMenuref.current && !KPIMenuref.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const proOptions = { hideAttribution: true };
  const [interactive, setInteractive] = useState(true);

  return (
    <div className="font-inter">
      <div className="flex justify-between items-center px-8 py-2.5">
        <div className="">
          <h2
            className={`font-medium ${
              showEditButton.includes(title) ? "text-[16px]" : "text-[18px]"
            }`}
          >
            {title}
          </h2>
          {showEditButton.includes(title) && (
            <p className="text-xs leading-3">{questionCombination[index]}</p>
          )}
        </div>
        {!showEditButton.includes(title) && (
          <h6 className="text-xs font-medium">{questionCombination[index]}</h6>
        )}
        <button
          className={`${
            !showEditButton.includes(title)
              ? "hidden"
              : "flex gap-[9px] bg-[#55C880] text-white rounded-[6px] px-[12px] py-1"
          }`}
          onClick={() => handleEditClick(title)}
        >
          <img src={edit} alt="" className="" />
          <span className="text-sm">Edit</span>
        </button>
      </div>
      <div className="relative h-[300px] border-t-[1.5px] border-[#D9D9D9]">
        <CustomControl
          setInteractive={setInteractive}
          interactive={interactive}
          showEditQuestions={showEditQuestions}
          treeType={title}
          showEditButton={showEditButton}
        />
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView={true}
          minZoom={0}
          proOptions={proOptions}
          zoomOnScroll={false}
          preventScrolling={false}
          onPaneClick={() => {
            setIsOpen(false);
          }}
          onNodeClick={() => {
            if (isOpen) {
              setIsOpen(false);
            }
          }}
          nodesDraggable={interactive}
          // onNodeContextMenu={onContextMenu}
          // onNodeClick={onContextMenu}
        ></ReactFlow>
      </div>
      <AddKPIModal
        show={showKPIFormModal}
        nodeData={currentNodeData}
        onClose={handleKPIModalClose}
      />
      {isOpen && (
        <div
          className="border border-[#D9D9D9] rounded-[6px] absolute top-12 bg-white"
          style={{
            top: position.y,
            left: position.x,
          }}
          ref={KPIMenuref}
        >
          <button
            className="flex items-center gap-2 px-3 py-2.5 text-[12px] font-medium"
            onClick={() => handleAddKPI(selectedNode._id, selectedNode.name)}
          >
            <img src={edit_pen} alt="" className="" />
            <span className="">Add/Edit KPIs</span>
          </button>
          <button
            className="flex items-center gap-2 px-3 py-2.5 w-[154px] text-[12px] font-medium border-t border-[#D9D9D9]"
            onClick={() => {
              setIsOpen(false);
              handleAddKPIMetric(selectedNode._id, selectedNode.name);
            }}
          >
            <img src={calendar_month} alt="" className="" />
            <span className="">Add Monthly Data</span>
          </button>
        </div>
      )}
      {showKPIMetricFormModal && (
        <AddKPIMetricModal
          show={showKPIMetricFormModal}
          nodeData={currentNodeData}
          onClose={handleKPIModalClose}
        />
      )}
    </div>
  );
};

const FlowWrapper = ({
  data,
  title,
  index,
  showEditButton,
  handleEditClick,
  showEditQuestions,
}) => {
  return (
    <ReactFlowProvider>
      <Flow
        treeData={data}
        title={title}
        index={index}
        showEditButton={showEditButton}
        handleEditClick={handleEditClick}
        showEditQuestions={showEditQuestions}
        // onMenuClick={handleMenuClick}
      />
    </ReactFlowProvider>
  );
};

const OrganizationTree = () => {
  const { user } = useUser();
  const { api } = useInterceptor();
  const [treeData, setTreeData] = useState([]);
  const [showEditButton, setShowEditButton] = useState([]);
  const [showEditQuestions, setShowEditQuestions] = useState([]);
  const [addKPIModal, setAddKPIModal] = useState("");
  const [showNoConfirmModal, setShowNoConfirmModal] = useState("");
  const [vsProgess, setVSProgess] = useState(0);
  const [organization, setOrganization] = useState(null);
  const [currVSStep, setCurrVSStep] = useState(1);
  const [currActivityStep, setCurrActivityStep] = useState(1);
  const [currFunctionStep, setCurrFunctionStep] = useState(1);
  const level1Ref = useRef(null);
  const level2Ref = useRef(null);
  const level3Ref = useRef(null);
  const level4Ref = useRef(null);
  const level5Ref = useRef(null);
  const level6Ref = useRef(null);
  const level7Ref = useRef(null);
  const [showEditSuccessModal, setShowEditSuccessModal] = useState(false);

  const handleAddKPIModal = (treeType) => {
    setAddKPIModal(treeType);
  };
  const handleCloseKPIModal = (treeType) => {
    setAddKPIModal("");
    setShowEditButton([...showEditButton, treeType]);
  };

  const handleEditClick = (treeType) => {
    setShowEditButton(showEditButton.filter((item) => item !== treeType));
    setShowEditQuestions([...showEditQuestions, treeType]);
  };

  const handleShowNoConfirmModal = (treeType) => {
    setShowNoConfirmModal(treeType);
  };
  const handleCloseNoConfirmModal = (treeType) => {
    setShowNoConfirmModal("");
    setShowEditQuestions([...showEditQuestions, treeType]);
  };

  const renderEditQuestions = (treeType) => {
    if (treeType === "Value-Stream View") {
      switch (currVSStep) {
        case 1:
          return <Level1 placement="edit" ref={level1Ref} />;
        case 2:
          return <Level5 placement="edit" ref={level5Ref} />;
        case 3:
          return <Level7 placement="edit" ref={level7Ref} />;
        default:
          return <Level1 placement="edit" ref={level1Ref} />;
      }
    } else if (treeType === "Activity View") {
      switch (currActivityStep) {
        case 1:
          return <Level2 placement="edit" ref={level2Ref} />;
        case 2:
          return <Level4 placement="edit" ref={level4Ref} />;
        default:
          return <Level2 placement="edit" ref={level2Ref} />;
      }
    } else {
      switch (currFunctionStep) {
        case 1:
          return <Level3 placement="edit" ref={level3Ref} />;
        case 2:
          return <Level6 placement="edit" ref={level6Ref} />;
        default:
          return <Level3 placement="edit" ref={level3Ref} />;
      }
    }
  };

  const dispatch = useDispatch();
  const [ids, setIds] = useState({});
  let level1Data = useSelector((state) => state.questionnaire.level1Data);
  let level2Data = useSelector((state) => state.questionnaire.level2Data);
  let level3Data = useSelector((state) => state.questionnaire.level3Data);
  let level4Data = useSelector((state) => state.questionnaire.level4Data);
  let level5Data = useSelector((state) => state.questionnaire.level5Data);
  let level6Data = useSelector((state) => state.questionnaire.level6Data);
  let level7Data = useSelector((state) => state.questionnaire.level7Data);

  const fetchTreeData = useCallback(async () => {
    try {
      const response = await api.get(`/getOrganizationTree`, {
        params: { organizationId: user.organizationId },
      });

      const treeData = response.data.data;
      console.log(response.data.data);

      for (let i = 0; i < 3; i++) {
        if (treeData[i].type === "Value-Stream View") {
          // redux state populating for value-stream in edit questions
          setIds((prev) => ({ ...prev, "value-stream": treeData[i]._id }));
          const level1Data = treeData[i].children.map((child) => ({
            _id: child._id,
            name: child.name,
          }));
          dispatch(updateLevel1(level1Data));

          const level5Data = [];
          treeData[i].children.forEach((segment) => {
            const product = segment.name;

            segment.children.forEach((valueStream) => {
              level5Data.push({
                _id: valueStream._id,
                name: valueStream.name,
                product: product,
              });
            });
          });
          dispatch(updateLevel5(level5Data));

          const level7Data = {};
          treeData[i].children.forEach((segment) => {
            segment.children.forEach((valueStream) => {
              const key = valueStream.name;
              level7Data[key] = [];
              valueStream.children.forEach((subActivity) => {
                level7Data[key] = [
                  ...level7Data[key],
                  { _id: subActivity._id, name: subActivity.name },
                ];
              });
            });
          });
          dispatch(updateLevel7(level7Data));
        } else if (treeData[i].type === "Activity View") {
          // redux state populating for activity view in edit questions
          setIds((prev) => ({ ...prev, "activity-view": treeData[i]._id }));
          const level2Data = treeData[i].children.map((activity) => ({
            _id: activity._id,
            name: activity.name,
          }));
          dispatch(updateLevel2(level2Data));

          const level4Data = new Array(
            treeData[i].children[0].children.length
          ).fill({
            _id: [],
            name: "",
          });

          treeData[i].children.forEach((activity) => {
            activity.children.forEach((subActivity, index) => {
              if (!level4Data[index]) {
                level4Data[index] = [{ _id: [], name: "" }];
              }
              level4Data[index] = {
                ...level4Data[index],
                _id: [...level4Data[index]._id, subActivity._id],
                name: subActivity.name,
              };
            });
          });
          dispatch(updateLevel4(level4Data));
        } else {
          // redux state populating for function view in edit questions
          setIds((prev) => ({ ...prev, "functional-view": treeData[i]._id }));
          const level3Data = treeData[i].children.map((unit) => ({
            _id: unit._id,
            name: unit.name,
          }));
          dispatch(updateLevel3(level3Data));

          const level6Data = {};
          treeData[i].children.forEach((unit) => {
            const key = unit.name;
            level6Data[key] = [];
            unit.children.forEach((department) => {
              level6Data[key] = [
                ...level6Data[key],
                { _id: department._id, name: department.name },
              ];
            });
          });
          dispatch(updateLevel6(level6Data));
        }
      }

      if (response.data.data.length > 3) {
        const data = response.data.data;

        const groupedByType = data.reduce((acc, item) => {
          // Group items by type (Activity-View, Value-Stream, Functional-View)
          if (!acc[item.type]) {
            acc[item.type] = [];
          }
          acc[item.type].push(item);
          return acc;
        }, {});

        const latestByType = {};

        for (const type in groupedByType) {
          // Sort each group by the 'createdAt' date in descending order
          groupedByType[type].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          // Take the latest entry (first one in the sorted array)
          latestByType[type] = groupedByType[type][0];
        }

        const result = Object.values(latestByType);
        setTreeData(result);

        console.log(result);
      } else if (response.data.success) {
        setTreeData(response.data.data);
      }
    } catch (error) {
      console.error(
        "Error fetching tree data:",
        error.response?.data?.message || error?.message
      );
    }
  }, [api, user.organizationId]);

  useEffect(() => {
    if (user?.organizationId) {
      fetchTreeData();
    }
  }, [user.organizationId, fetchTreeData]);

  const getOrganization = async () => {
    try {
      const res = await api.get(
        `/organization/getorganization/${user.organizationId}`
      );
      if (res.status === 200) {
        setOrganization(res.data.data);
      }
      // console.log("res -> ", res);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    getOrganization();
  }, [user]);

  const payloadCreation = (type) => {
    if (type === "Value-Stream View") {
      return {
        _id: ids["value-stream"],
        type: "Value-Stream View",
        name: organization?.name || "Aceable, Inc.",
        organizationId: user.organizationId,
        children: level1Data.map((segment) => ({
          _id: segment._id,
          name: segment.name,
          children: level5Data
            .filter((stream) => stream.product === segment.name)
            .map((stream) => ({
              _id: stream._id,
              name: stream.name,
              children:
                level7Data[stream.name]?.map((activity) => ({
                  _id: activity._id,
                  name: activity.name,
                })) || [],
            })),
        })),
      };
    } else if (type === "Activity View") {
      return {
        _id: ids["activity-view"],
        type: "Activity View",
        name: organization?.name || "Aceable, Inc.",
        organizationId: user.organizationId,
        children: level2Data.map((activity, index) => ({
          _id: activity._id,
          name: activity.name,
          children: level4Data.map((subActivity) => ({
            _id: subActivity._id[index],
            name: subActivity.name,
          })),
        })),
      };
    } else {
      console.log(level6Data);
      return {
        _id: ids["functional-view"],
        type: "Functional View",
        name: organization?.name || "Aceable, Inc.",
        organizationId: user.organizationId,
        children: level3Data.map((functionalUnit) => ({
          _id: functionalUnit._id,
          name: functionalUnit.name,
          children:
            level6Data[functionalUnit.name]?.map((department) => ({
              _id: department._id,
              name: department.name,
            })) || [],
        })),
      };
    }
  };
  const handleNextClick = async (treeType) => {
    if (treeType === "Value-Stream View") {
      let error;
      if (level1Ref.current) {
        error = level1Ref.current.handleNextClick();
      } else if (level5Ref.current) {
        error = level5Ref.current.handleNextClick();
      } else if (level7Ref.current) {
        error = level7Ref.current.handleSubmit();
      }
      if (currVSStep <= 2) {
        if (!error) {
          setCurrVSStep(currVSStep + 1);
        } else {
          return;
        }
      } else {
        if (!error) {
          setShowEditSuccessModal(treeType);
        } else {
          return;
        }
      }
    } else if (treeType === "Activity View") {
      let error;
      if (level2Ref.current) {
        error = level2Ref.current.handleNextClick();
      } else if (level4Ref.current) {
        error = level4Ref.current.handleNextClick();
      }
      if (currActivityStep <= 1) {
        if (!error) {
          setCurrActivityStep(currActivityStep + 1);
        } else {
          return;
        }
      } else {
        if (!error) {
          setShowEditSuccessModal(treeType);
        } else {
          return;
        }
      }
    } else {
      let error;
      if (level3Ref.current) {
        error = level3Ref.current.handleNextClick();
      } else if (level6Ref.current) {
        error = level6Ref.current.handleNext();
      }
      if (currFunctionStep <= 1) {
        if (!error) {
          setCurrFunctionStep(currFunctionStep + 1);
        } else {
          return;
        }
      } else {
        if (!error) {
          setShowEditSuccessModal(treeType);
        } else {
          return;
        }
      }
    }
  };
  const onClose = async (treeType) => {
    if (treeType === "Value-Stream View") {
      const valueStreamPayload = payloadCreation(treeType);
      let response = await api.put("/updateTree", valueStreamPayload);
      if (response.status === 200) {
        window.location.reload();
      }
    } else if (treeType === "Activity View") {
      const activityViewPayload = payloadCreation(treeType);
      console.log("activityViewPayload -> ", activityViewPayload);
      let response = await api.put("/updateTree", activityViewPayload);
      if (response.status === 200) {
        window.location.reload();
      }
    } else {
      const functionalViewPayload = payloadCreation(treeType);
      let response = await api.put("/updateTree", functionalViewPayload);
      if (response.status === 200) {
        window.location.reload();
      }
    }
  };

  const handleBackClick = (treeType) => {
    if (treeType === "Value-Stream View") {
      setCurrVSStep(currVSStep - 1);
    } else if (treeType === "Activity View") {
      setCurrActivityStep(currActivityStep - 1);
    } else {
      setCurrFunctionStep(currFunctionStep - 1);
    }
  };

  const PriorityOrder = [
    "Value-Stream View",
    "Activity View",
    "Functional View",
  ];

  return (
    <div className="">
      <Header />
      <div className="max-w-[1200px] max-xl:px-5 mx-auto font-inter">
        {treeData
          .sort((a, b) => {
            const indexOfa = PriorityOrder.indexOf(a.type);
            const indexOfb = PriorityOrder.indexOf(b.type);
            return indexOfa - indexOfb;
          })
          .map((tree, index) => (
            <React.Fragment key={tree._id}>
              <div
                className={`bg-[#F4F5F6] w-full font-inter rounded-[10px] mt-[30px] mb-5`}
              >
                <FlowWrapper
                  data={tree}
                  title={tree.type}
                  index={index}
                  showEditButton={showEditButton}
                  showEditQuestions={showEditQuestions}
                  handleEditClick={handleEditClick}
                />

                <div className="pt-7 pb-10">
                  <div
                    className={`${
                      !showEditButton.includes(tree.type) &&
                      !showEditQuestions.includes(tree.type)
                        ? "flex justify-between max-w-[1062px] mx-auto bg-[#F9FFFE] rounded-[10px] items-center py-3 px-5"
                        : "hidden"
                    }`}
                  >
                    <p className="text-sm font-semibold">
                      {tree.type === "Value-Stream View"
                        ? "Does this structure accurately represent your firm segments and value-streams?"
                        : tree.type === "Activity View"
                        ? "Does this structure accurately represent your firm segments and value-streams?"
                        : "Does this structure accurately represent your firm's functions and departments?"}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        // disabled
                        className="border-[1.5px] text-sm font-medium border-[#55C880] rounded-[10px] text-[#55C880] px-4 py-[5px]"
                        onClick={() => handleShowNoConfirmModal(tree.type)}
                      >
                        No
                      </button>
                      <button
                        // disabled
                        className="text-white text-sm rounded-[10px] bg-[#55C880] px-4 py-[6px]"
                        onClick={() => handleAddKPIModal(tree.type)}
                      >
                        Yes
                      </button>
                    </div>
                    {addKPIModal === tree.type && (
                      <ConfirmedViewChart
                        viewtype={addKPIModal}
                        onClose={handleCloseKPIModal}
                      />
                    )}
                    {showNoConfirmModal === tree.type && (
                      <NoConfirmVSView
                        treeType={showNoConfirmModal}
                        onClose={handleCloseNoConfirmModal}
                      />
                    )}
                  </div>
                </div>
              </div>
              {showEditQuestions.includes(tree.type) && (
                <div className="">
                  <div className="border border-[#D9D9D9] rounded-[10px]">
                    <div className="flex justify-between px-10 mt-7 mb-5">
                      <h1 className="text-[18px] font-semibold">
                        Set Up {tree.type}
                      </h1>
                      <div className="rounded-full relative p-2 w-[39px] h-[39px] max-md:hidden">
                        <div className="w-full h-full rounded-full bg-gray-100"></div>

                        <div
                          className="absolute top-0 left-0 w-full h-full"
                          style={{
                            background: `conic-gradient(rgb(20 83 45) ${
                              //   currVSStep * 33.3333
                              (tree.type === "Value-Stream View"
                                ? currVSStep
                                : tree.type === "Activity View"
                                ? currActivityStep
                                : currFunctionStep) *
                              (tree.type === "Value-Stream View" ? 33.3333 : 50)
                            }%, rgba(235, 230, 230, 1) ${
                              (tree.type === "Value-Stream View"
                                ? currVSStep
                                : tree.type === "Activity View"
                                ? currActivityStep
                                : currFunctionStep) *
                              (tree.type === "Value-Stream View" ? 33.3333 : 50)
                            }%)`,
                            borderRadius: "100%",
                          }}
                        ></div>

                        <div
                          className="absolute top-0 left-0 w-full h-full rounded-full bg-white flex items-center justify-center m-[4px]"
                          style={{
                            width: "calc(100% - 8px)",
                            height: "calc(100% - 8px)",
                          }}
                        >
                          <span className="text-[12px] font-medium">
                            {tree.type === "Value-Stream View"
                              ? currVSStep
                              : tree.type === "Activity View"
                              ? currActivityStep
                              : currFunctionStep}
                            /{tree.type === "Value-Stream View" ? "3" : "2"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {renderEditQuestions(tree.type)}
                  </div>
                  <div
                    className={`flex mt-[26px] ${
                      (tree.type === "Value-Stream View"
                        ? currVSStep
                        : tree.type === "Activity View"
                        ? currActivityStep
                        : currFunctionStep) === 1
                        ? "justify-end"
                        : "justify-between"
                    } `}
                  >
                    {(tree.type === "Value-Stream View"
                      ? currVSStep
                      : tree.type === "Activity View"
                      ? currActivityStep
                      : currFunctionStep) > 1 && (
                      <button
                        className="border border-[#D9D9D9] text-[#1C3F3A] flex gap-3 justify-center items-center py-[0.825rem] rounded-[10px] font-inter font-medium px-5 hover:bg-[#f0f0f0] transition-colors"
                        onClick={() => handleBackClick(tree.type)}
                      >
                        <svg
                          width="17"
                          height="8"
                          viewBox="0 0 17 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.80775 0.192239L4.52125 0.899989L1.92125 3.49999H17V4.49999H1.92125L4.5155 7.09999L3.802 7.80774L0 3.99999L3.80775 0.192239Z"
                            fill="#1C3F3A"
                          />
                        </svg>
                        Back
                      </button>
                    )}
                    <button
                      className="bg-[#55C880] flex items-center justify-center gap-3 text-white pl-5 pr-6 py-[0.825rem] rounded-[10px] overflow-hidden"
                      onClick={() => handleNextClick(tree.type)}
                    >
                      {tree.type === "Value-Stream View"
                        ? currVSStep === 3
                          ? "Update"
                          : "Next"
                        : tree.type === "Activity View"
                        ? currActivityStep === 2
                          ? "Update"
                          : "Next"
                        : currFunctionStep === 2
                        ? "Update"
                        : "Next"}
                      <svg
                        width="17"
                        height="8"
                        viewBox="0 0 17 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.1923 7.80776L12.4788 7.10001L15.0788 4.50001H0V3.50001H15.0788L12.4845 0.900011L13.198 0.192261L17 4.00001L13.1923 7.80776Z"
                          fill="white"
                        />
                      </svg>
                    </button>
                    {showEditSuccessModal === tree.type && (
                      <SuccessEdit treeType={tree.type} onClose={onClose} />
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
      </div>
      <Footer />
    </div>
  );
};

export default OrganizationTree;
